import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Video, Image, CheckCircle, Loader2, Trash2, BarChart3, BookMarked, Settings2, BookOpen, RefreshCw, Zap, Coins, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import InfographicUploadPanel from "@/components/admin/InfographicUploadPanel";
import InfographicGrid, { type InfographicAsset } from "@/components/admin/InfographicGrid";
import InfographicPreviewModal from "@/components/admin/InfographicPreviewModal";
import VideoAssetGrid, { type VideoAsset } from "@/components/admin/VideoAssetGrid";
import BulkMediaUploadPanel, { type UploadResult } from "@/components/admin/BulkMediaUploadPanel";
import MissingLessonMediaPanel from "@/components/admin/MissingLessonMediaPanel";
import { useAdminRole } from "@/hooks/useAdminRole";
import MasteryCoveragePanel from "@/components/admin/MasteryCoveragePanel";
import RefinementCoveragePanel from "@/components/admin/RefinementCoveragePanel";
import VocabAdminPanel from "@/components/admin/VocabAdminPanel";
import ExampleRefreshPanel from "@/components/admin/ExampleRefreshPanel";
import EventRewardAdminPanel from "@/components/admin/EventRewardAdminPanel";
import RewardRulesPanel from "@/components/admin/RewardRulesPanel";
import { findBestMatch } from "@/utils/infographicMatcher";
import MediaDiagnosticsPanel from "@/components/admin/MediaDiagnosticsPanel";
import { getErrorMessage } from "@/lib/errors";
import type { AnyValue } from "@/lib/rowTypes";

interface Lesson {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
  lesson_order: number;
  lesson_video_url: string | null;
  lesson_infographic_url: string | null;
}

type MediaKind = "infographic" | "video";
type ManagedAsset = InfographicAsset | VideoAsset;

const MEDIA_CONFIG = {
  infographic: {
    tableName: "infographic_assets",
    lessonField: "lesson_infographic_url",
    storageBucket: "infographics",
    storageUrlSegment: "/storage/v1/object/public/infographics/",
    successLabel: "Infographic",
  },
  video: {
    tableName: "video_assets",
    lessonField: "lesson_video_url",
    storageBucket: "lesson-media",
    storageUrlSegment: "/storage/v1/object/public/lesson-media/",
    successLabel: "Video",
  },
} as const;

const AdminUpload = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useAdminRole();
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [assets, setAssets] = useState<InfographicAsset[]>([]);
  const [videoAssets, setVideoAssets] = useState<VideoAsset[]>([]);
  const [previewAsset, setPreviewAsset] = useState<InfographicAsset | null>(null);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [uploadedNames, setUploadedNames] = useState<Record<string, string>>({});
  const [dragOver, setDragOver] = useState<string | null>(null);

  const loadLessons = useCallback(async () => {
    const { data, error } = await supabase
      .from("lessons")
      .select("*")
      .order("course_name")
      .order("lesson_order");

    if (error) {
      console.error("Failed to load lessons", error);
      return;
    }

    setLessons(data || []);
  }, []);

  const loadAssets = useCallback(async () => {
    const { data, error } = await supabase
      .from("infographic_assets")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Failed to load infographic assets", error);
      return;
    }

    setAssets(((data ?? []) as AnyValue) as InfographicAsset[]);
  }, []);

  const loadVideoAssets = useCallback(async () => {
    const { data, error } = await supabase
      .from("video_assets")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Failed to load video assets", error);
      return;
    }

    setVideoAssets(((data ?? []) as AnyValue) as VideoAsset[]);
  }, []);

  useEffect(() => {
    if (adminLoading || !isAdmin) return;
    void loadLessons();
    void loadAssets();
    void loadVideoAssets();
  }, [adminLoading, isAdmin, loadLessons, loadAssets, loadVideoAssets]);

  const refreshMedia = useCallback((kind: MediaKind) => {
    if (kind === "infographic") {
      void loadAssets();
      return;
    }
    void loadVideoAssets();
  }, [loadAssets, loadVideoAssets]);

  const getAssetsForKind = useCallback((kind: MediaKind): ManagedAsset[] => {
    return kind === "infographic" ? assets : videoAssets;
  }, [assets, videoAssets]);

  const handleBulkUploadComplete = useCallback(async (kind: MediaKind, results: UploadResult[]) => {
    try {
      const config = MEDIA_CONFIG[kind];
      const mediaAssets = getAssetsForKind(kind);
      const newInserts: AnyValue[] = [];
      const relinkedLessonUpdates: { lessonId: string; fileUrl: string }[] = [];

      for (const result of results) {
        const match = findBestMatch(result.fileName, lessons);
        const shouldAutoAssign = match && match.confidence === "high";

        if (result.status === "reused") {
          // File already exists in asset table — just ensure the lesson link if auto-match
          if (shouldAutoAssign) {
            // Find the existing asset row for this file
            const existingAsset = mediaAssets.find(
              (a) => a.file_name.toLowerCase() === result.fileName.toLowerCase()
            );
            if (existingAsset) {
              // Update the existing asset row to point at this lesson
              await supabase
                .from(config.tableName)
                .update({
                  assigned_lesson_id: match.lessonId,
                  assigned_course_name: match.courseName,
                  status: "assigned",
                  match_confidence: "high",
                  updated_at: new Date().toISOString(),
                })
                .eq("id", existingAsset.id);
            }
            relinkedLessonUpdates.push({ lessonId: match.lessonId, fileUrl: result.fileUrl || existingAsset?.file_url || "" });
          }
          continue;
        }

        // New upload — create asset row
        newInserts.push({
          file_name: result.fileName,
          file_url: result.fileUrl,
          file_size: result.fileSize,
          mime_type: result.mimeType,
          assigned_lesson_id: shouldAutoAssign ? match.lessonId : null,
          assigned_course_name: match ? match.courseName : null,
          match_confidence: match?.confidence || "none",
          status: shouldAutoAssign ? "assigned" : match && match.confidence !== "none" ? "needs-review" : "unassigned",
        });
      }

      if (newInserts.length > 0) {
        const { error: insertError } = await supabase.from(config.tableName).insert(newInserts);
        if (insertError) throw insertError;
      }

      // Update lessons for both new auto-assigned and relinked files
      const allLessonUpdates = [
        ...newInserts.filter((item) => item.status === "assigned" && item.assigned_lesson_id).map((item) => ({
          lessonId: item.assigned_lesson_id as string,
          fileUrl: item.file_url as string,
        })),
        ...relinkedLessonUpdates,
      ];

      if (allLessonUpdates.length > 0) {
        // Propagate media to all lessons with the same name (cross-course sync)
        const expandedUpdates: { lessonId: string; fileUrl: string }[] = [];
        for (const update of allLessonUpdates) {
          const targetLesson = lessons.find((l) => l.lesson_id === update.lessonId);
          if (targetLesson) {
            const duplicates = lessons.filter((l) => l.lesson_name === targetLesson.lesson_name);
            for (const dup of duplicates) {
              expandedUpdates.push({ lessonId: dup.lesson_id, fileUrl: update.fileUrl });
            }
          } else {
            expandedUpdates.push(update);
          }
        }

        // Deduplicate by lessonId
        const uniqueUpdates = Array.from(new Map(expandedUpdates.map((u) => [u.lessonId, u])).values());

        await Promise.all(
          uniqueUpdates.map((update) =>
            supabase
              .from("lessons")
              .update({ [config.lessonField]: update.fileUrl } as AnyValue)
              .eq("lesson_id", update.lessonId)
          )
        );
      }

      const newCount = newInserts.length;
      const relinkedCount = relinkedLessonUpdates.length;
      const autoCount = newInserts.filter((item) => item.status === "assigned").length;
      const reviewCount = newInserts.filter((item) => item.status === "needs-review").length;

      const parts: string[] = [];
      if (autoCount) parts.push(`${autoCount} auto-assigned`);
      if (relinkedCount) parts.push(`${relinkedCount} relinked`);
      if (reviewCount) parts.push(`${reviewCount} need review`);
      const unassignedCount = newInserts.filter((item) => item.status === "unassigned").length;
      if (unassignedCount) parts.push(`${unassignedCount} unassigned`);

      if (parts.length > 0) {
        toast.info(`Processed ${newCount + relinkedCount} ${config.successLabel.toLowerCase()} file(s): ${parts.join(", ")}`);
      }

      refreshMedia(kind);
      void loadLessons();
    } catch (err: unknown) {
      toast.error(`Couldn't save uploaded ${kind}s: ${getErrorMessage(err)}`);
    }
  }, [lessons, loadLessons, refreshMedia, getAssetsForKind]);

  const handleAssignAsset = useCallback(async (kind: MediaKind, assetId: string, lessonId: string, courseName: string) => {
    const config = MEDIA_CONFIG[kind];
    const mediaAssets = getAssetsForKind(kind);
    const asset = mediaAssets.find((item) => item.id === assetId);
    if (!asset) return;

    const lesson = lessons.find((item) => item.lesson_id === lessonId);
    const lessonAlreadyAssigned = kind === "infographic" ? lesson?.lesson_infographic_url : lesson?.lesson_video_url;

    if (lessonAlreadyAssigned) {
      const existing = mediaAssets.find((item) => item.assigned_lesson_id === lessonId && item.id !== assetId);
      if (existing) {
        await supabase
          .from(config.tableName)
          .update({
            assigned_lesson_id: null,
            assigned_course_name: null,
            status: "unassigned",
            match_confidence: "none",
            updated_at: new Date().toISOString(),
          })
          .eq("id", existing.id);
      }
    }

    await supabase
      .from(config.tableName)
      .update({
        assigned_lesson_id: lessonId,
        assigned_course_name: courseName,
        status: "assigned",
        match_confidence: "high",
        updated_at: new Date().toISOString(),
      })
      .eq("id", assetId);

    // Update this lesson AND all other lessons with the same name (media sync)
    const targetLesson = lessons.find((l) => l.lesson_id === lessonId);
    const duplicateLessons = targetLesson
      ? lessons.filter((l) => l.lesson_name === targetLesson.lesson_name)
      : [{ lesson_id: lessonId }];

    await Promise.all(
      duplicateLessons.map((l) =>
        supabase
          .from("lessons")
          .update({ [config.lessonField]: asset.file_url } as AnyValue)
          .eq("lesson_id", l.lesson_id)
      )
    );

    toast.success(`${config.successLabel} assigned to lesson`);
    refreshMedia(kind);
    void loadLessons();
  }, [getAssetsForKind, lessons, loadLessons, refreshMedia]);

  const handleUnassignAsset = useCallback(async (kind: MediaKind, assetId: string) => {
    const config = MEDIA_CONFIG[kind];
    const mediaAssets = getAssetsForKind(kind);
    const asset = mediaAssets.find((item) => item.id === assetId);
    if (!asset) return;

    if (asset.assigned_lesson_id) {
      await supabase
        .from("lessons")
        .update({ [config.lessonField]: null } as AnyValue)
        .eq("lesson_id", asset.assigned_lesson_id);
    }

    await supabase
      .from(config.tableName)
      .update({
        assigned_lesson_id: null,
        assigned_course_name: null,
        status: "unassigned",
        match_confidence: "none",
        updated_at: new Date().toISOString(),
      })
      .eq("id", assetId);

    toast.success(`${config.successLabel} unassigned`);
    refreshMedia(kind);
    void loadLessons();
  }, [getAssetsForKind, loadLessons, refreshMedia]);

  const handleDeleteAsset = useCallback(async (kind: MediaKind, assetId: string) => {
    const config = MEDIA_CONFIG[kind];
    const mediaAssets = getAssetsForKind(kind);
    const asset = mediaAssets.find((item) => item.id === assetId);
    if (!asset) return;

    if (asset.assigned_lesson_id) {
      await supabase
        .from("lessons")
        .update({ [config.lessonField]: null } as AnyValue)
        .eq("lesson_id", asset.assigned_lesson_id);
    }

    try {
      const url = new URL(asset.file_url);
      const pathParts = url.pathname.split(config.storageUrlSegment);
      if (pathParts[1]) {
        await supabase.storage.from(config.storageBucket).remove([decodeURIComponent(pathParts[1])]);
      }
    } catch {
      // noop
    }

    await supabase.from(config.tableName).delete().eq("id", assetId);
    toast.success(`${config.successLabel} deleted`);
    refreshMedia(kind);
    void loadLessons();
  }, [getAssetsForKind, loadLessons, refreshMedia]);

  const uploadFile = async (lessonId: string, file: File, type: "video" | "infographic") => {
    const key = `${lessonId}-${type}`;
    setUploading((prev) => ({ ...prev, [key]: true }));
    try {
      const ext = file.name.split(".").pop();
      const path = `${type}s/${lessonId}.${ext}`;
      const { error: uploadError } = await supabase.storage.from("lesson-media").upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from("lesson-media").getPublicUrl(path);
      const updateField = type === "video" ? { lesson_video_url: urlData.publicUrl } : { lesson_infographic_url: urlData.publicUrl };
      const { error: updateError } = await supabase.from("lessons").update(updateField).eq("lesson_id", lessonId);
      if (updateError) throw updateError;
      toast.success(`${type === "video" ? "Video" : "Infographic"} uploaded!`);
      setUploadedNames((prev) => ({ ...prev, [key]: file.name }));
      void loadLessons();
    } catch (err: unknown) {
      toast.error(`Upload failed: ${getErrorMessage(err)}`);
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const removeMedia = async (lessonId: string, type: "video" | "infographic") => {
    const key = `${lessonId}-${type}`;
    setUploading((prev) => ({ ...prev, [key]: true }));
    try {
      const updateField = type === "video" ? { lesson_video_url: null } : { lesson_infographic_url: null };
      const { error } = await supabase.from("lessons").update(updateField).eq("lesson_id", lessonId);
      if (error) throw error;
      toast.success(`${type === "video" ? "Video" : "Infographic"} removed.`);
      void loadLessons();
    } catch (err: unknown) {
      toast.error(`Failed: ${getErrorMessage(err)}`);
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleDrop = (e: React.DragEvent, lessonId: string, type: "video" | "infographic") => {
    e.preventDefault();
    setDragOver(null);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(lessonId, file, type);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, lessonId: string, type: "video" | "infographic") => {
    const file = e.target.files?.[0];
    if (file) uploadFile(lessonId, file, type);
    e.target.value = "";
  };

  const grouped = lessons.reduce<Record<string, Lesson[]>>((acc, lesson) => {
    (acc[lesson.course_name] = acc[lesson.course_name] || []).push(lesson);
    return acc;
  }, {});

  const courseNames = [...new Set(lessons.map((lesson) => lesson.course_name))].sort();

  if (adminLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50">
          <div className="container max-w-5xl mx-auto flex items-center gap-3 py-4 px-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="font-heading font-bold text-lg text-foreground">Media Manager</h1>
          </div>
        </header>

        <main className="container max-w-5xl mx-auto py-6 px-4">
          <div className="rounded-xl border border-border bg-card p-6 text-center">
            <p className="font-body text-sm text-foreground">You need admin access to manage lesson media.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container max-w-5xl mx-auto flex items-center gap-3 py-4 px-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="font-heading font-bold text-lg text-foreground">Media Manager</h1>
        </div>
      </header>

      <main className="container max-w-5xl mx-auto py-6 px-4">
        <Tabs defaultValue="infographics" className="space-y-6">
          <TabsList className="bg-card border border-border flex flex-wrap h-auto">
            <TabsTrigger value="infographics" className="gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" />
              Infographics
            </TabsTrigger>
            <TabsTrigger value="videos" className="gap-1.5">
              <Video className="w-3.5 h-3.5" />
              Videos
            </TabsTrigger>
            <TabsTrigger value="per-lesson" className="gap-1.5">
              <Image className="w-3.5 h-3.5" />
              Per-Lesson Media
            </TabsTrigger>
            <TabsTrigger value="mastery" className="gap-1.5">
              <BookMarked className="w-3.5 h-3.5" />
              Mastery Coverage
            </TabsTrigger>
            <TabsTrigger value="refinements" className="gap-1.5">
              <Settings2 className="w-3.5 h-3.5" />
              Refinements
            </TabsTrigger>
            <TabsTrigger value="vocabulary" className="gap-1.5">
              <BookOpen className="w-3.5 h-3.5" />
              Vocabulary
            </TabsTrigger>
            <TabsTrigger value="examples" className="gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" />
              Examples
            </TabsTrigger>
            <TabsTrigger value="events" className="gap-1.5">
              <Zap className="w-3.5 h-3.5" />
              Events
            </TabsTrigger>
            <TabsTrigger value="rewards" className="gap-1.5">
              <Coins className="w-3.5 h-3.5" />
              Rewards
            </TabsTrigger>
            <TabsTrigger value="diagnostics" className="gap-1.5">
              <Stethoscope className="w-3.5 h-3.5" />
              Diagnostics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="infographics" className="space-y-8">
            <section className="space-y-3">
              <h2 className="font-heading font-semibold text-sm text-foreground">Upload Infographics</h2>
              <InfographicUploadPanel
                onUploadComplete={(results) => handleBulkUploadComplete("infographic", results)}
                existingFileNames={assets.map((asset) => asset.file_name)}
                existingAssets={assets.map((asset) => ({ fileName: asset.file_name, fileUrl: asset.file_url }))}
              />
            </section>

            <MissingLessonMediaPanel lessons={lessons} mediaType="infographic" />

            <section className="space-y-3">
              <h2 className="font-heading font-semibold text-sm text-foreground">Uploaded Assets</h2>
              <InfographicGrid
                assets={assets}
                lessons={lessons}
                courseNames={courseNames}
                onAssign={(assetId, lessonId, courseName) => handleAssignAsset("infographic", assetId, lessonId, courseName)}
                onUnassign={(assetId) => handleUnassignAsset("infographic", assetId)}
                onDelete={(assetId) => handleDeleteAsset("infographic", assetId)}
                onPreview={setPreviewAsset}
              />
            </section>
          </TabsContent>

          <TabsContent value="videos" className="space-y-8">
            <section className="space-y-3">
              <h2 className="font-heading font-semibold text-sm text-foreground">Upload Videos</h2>
              <BulkMediaUploadPanel
                bucketName="lesson-media"
                uploadPathPrefix="bulk-videos"
                accept="video/mp4,video/webm,video/quicktime"
                validMimeTypes={["video/mp4", "video/webm", "video/quicktime"]}
                mediaLabel="Videos"
                helperText="MP4, WEBM, MOV — Upload one or many files at once and auto-match them to lessons"
                existingFileNames={videoAssets.map((asset) => asset.file_name)}
                existingAssets={videoAssets.map((asset) => ({ fileName: asset.file_name, fileUrl: asset.file_url }))}
                onUploadComplete={(results) => handleBulkUploadComplete("video", results)}
              />
            </section>

            <MissingLessonMediaPanel lessons={lessons} mediaType="video" />

            <section className="space-y-3">
              <h2 className="font-heading font-semibold text-sm text-foreground">Uploaded Videos</h2>
              <VideoAssetGrid
                assets={videoAssets}
                lessons={lessons}
                courseNames={courseNames}
                onAssign={(assetId, lessonId, courseName) => handleAssignAsset("video", assetId, lessonId, courseName)}
                onUnassign={(assetId) => handleUnassignAsset("video", assetId)}
                onDelete={(assetId) => handleDeleteAsset("video", assetId)}
              />
            </section>
          </TabsContent>

          <TabsContent value="per-lesson" className="space-y-10">
            {Object.entries(grouped).map(([courseName, courseLessons]) => (
              <section key={courseName}>
                <h2 className="font-heading font-semibold text-base text-foreground mb-4">{courseName}</h2>
                <div className="space-y-4">
                  {courseLessons.map((lesson) => (
                    <div key={lesson.lesson_id} className="rounded-xl border border-border bg-card p-4 space-y-3">
                      <h3 className="font-body font-medium text-sm text-foreground">
                        {lesson.lesson_order}. {lesson.lesson_name}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <DropZone
                          label="Video"
                          icon={<Video className="w-5 h-5" />}
                          hasFile={!!lesson.lesson_video_url}
                          displayName={uploadedNames[`${lesson.lesson_id}-video`] || (lesson.lesson_video_url ? "✓ Video uploaded" : undefined)}
                          loading={!!uploading[`${lesson.lesson_id}-video`]}
                          dragActive={dragOver === `${lesson.lesson_id}-video`}
                          accept="video/*"
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDragOver(`${lesson.lesson_id}-video`);
                          }}
                          onDragLeave={() => setDragOver(null)}
                          onDrop={(e) => handleDrop(e, lesson.lesson_id, "video")}
                          onFileSelect={(e) => handleFileSelect(e, lesson.lesson_id, "video")}
                          onRemove={() => removeMedia(lesson.lesson_id, "video")}
                        />
                        <DropZone
                          label="Infographic"
                          icon={<Image className="w-5 h-5" />}
                          hasFile={!!lesson.lesson_infographic_url}
                          displayName={uploadedNames[`${lesson.lesson_id}-infographic`] || (lesson.lesson_infographic_url ? "✓ Infographic uploaded" : undefined)}
                          loading={!!uploading[`${lesson.lesson_id}-infographic`]}
                          dragActive={dragOver === `${lesson.lesson_id}-infographic`}
                          accept="image/*"
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDragOver(`${lesson.lesson_id}-infographic`);
                          }}
                          onDragLeave={() => setDragOver(null)}
                          onDrop={(e) => handleDrop(e, lesson.lesson_id, "infographic")}
                          onFileSelect={(e) => handleFileSelect(e, lesson.lesson_id, "infographic")}
                          onRemove={() => removeMedia(lesson.lesson_id, "infographic")}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
            {lessons.length === 0 && <p className="text-muted-foreground text-center py-12">No lessons found.</p>}
          </TabsContent>

          <TabsContent value="mastery" className="space-y-6">
            <MasteryCoveragePanel />
          </TabsContent>

          <TabsContent value="refinements" className="space-y-6">
            <RefinementCoveragePanel />
          </TabsContent>

          <TabsContent value="vocabulary" className="space-y-6">
            <VocabAdminPanel />
          </TabsContent>

          <TabsContent value="examples" className="space-y-6">
            <ExampleRefreshPanel />
          </TabsContent>

          <TabsContent value="events" className="space-y-6">
            <EventRewardAdminPanel />
          </TabsContent>

          <TabsContent value="rewards" className="space-y-6">
            <RewardRulesPanel />
          </TabsContent>
          <TabsContent value="diagnostics" className="space-y-6">
            <MediaDiagnosticsPanel
              lessons={lessons}
              onRepairComplete={() => { void loadLessons(); void loadAssets(); void loadVideoAssets(); }}
            />
          </TabsContent>
        </Tabs>
      </main>

      <InfographicPreviewModal
        asset={previewAsset}
        assets={assets}
        onClose={() => setPreviewAsset(null)}
        onNavigate={(asset) => setPreviewAsset(asset as InfographicAsset)}
      />
    </div>
  );
};

interface DropZoneProps {
  label: string;
  icon: React.ReactNode;
  hasFile: boolean;
  displayName?: string;
  loading: boolean;
  dragActive: boolean;
  accept: string;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
}

function DropZone({ label, icon, hasFile, displayName, loading, dragActive, accept, onDragOver, onDragLeave, onDrop, onFileSelect, onRemove }: DropZoneProps) {
  const id = `file-${label}-${Math.random()}`;

  return (
    <div
      className={`relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
        dragActive ? "border-primary bg-primary/5" : hasFile ? "border-accent/50 bg-accent/5" : "border-border hover:border-muted-foreground/40"
      }`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : hasFile ? <CheckCircle className="w-5 h-5 text-accent" /> : icon}
      <span className="text-xs text-muted-foreground font-body text-center truncate max-w-full px-2">
        {loading ? "Uploading…" : hasFile ? displayName || `${label} uploaded` : `Drop ${label.toLowerCase()} or click`}
      </span>
      {!loading && !hasFile && (
        <label htmlFor={id} className="cursor-pointer text-xs font-medium text-primary hover:underline">
          Browse
          <input id={id} type="file" accept={accept} className="hidden" onChange={onFileSelect} />
        </label>
      )}
      {hasFile && !loading && (
        <button onClick={onRemove} className="text-xs text-destructive hover:underline flex items-center gap-1">
          <Trash2 className="w-3 h-3" /> Remove
        </button>
      )}
    </div>
  );
}

export default AdminUpload;
